import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useRequestPhoneChangeMutation,
  useUploadAvatarMutation,
} from '@/redux/internal-services/usersApi';
import { getErrorDetail } from '@/utils/apiError';
import { useGetCountriesQuery } from '@/redux/internal-services/geoApi';
import { splitPhone, normalizeDialCode } from '@/utils/phone';
import { DialCodeSelect } from '@/components/UI/GeoSelect';
import { BackIcon, NextIcon } from '@/components/Onboarding/StepShell';
import { Button } from '@/components/UI/Button';
import { fieldClass } from '@/components/UI/fieldStyles';
import { cn } from '@/utils/cn';

interface ProfileStepProps {
  name: string;
  /** The user's account email — already verified at signup/invite; shown read-only. */
  email: string;
  /** The user's assigned role label — shown read-only (assigned at signup/invite). */
  roleName?: string;
  /** The user's current phone in full international form (null when none set yet). */
  phone: string | null;
  /** Whether the current phone has been verified. */
  phoneVerified: boolean;
  avatarUrl: string;
  onNameChange: (v: string) => void;
  onAvatarChange: (url: string) => void;
  /**
   * Advance from the profile step. `newPhone` is the full international number to verify when the
   * user entered an unverified number (the code has already been sent → parent shows the
   * verification screen), or null when no phone verification is needed.
   */
  onProceed: (newPhone: string | null) => void;
  /** Go back to the previous guided-onboarding step (company setup). */
  onBack?: () => void;
  saving?: boolean;
  /**
   * Optional controlled phone draft (full international form, e.g. "+15551234567").
   * When provided the parent owns the draft value; the voice agent can fill it externally.
   * Falls back to internal state when absent (no breaking change for existing callers).
   */
  phoneValue?: string;
  onPhoneValueChange?: (v: string) => void;
  /**
   * Optional controlled dial code (e.g. "+1"), set separately by the voice agent. When provided it
   * drives the country-code dropdown directly — no parsing needed. `phoneValue` then carries just
   * the local number (though a full international number there is still parsed as a fallback).
   */
  countryCodeValue?: string;
  onSkip?: () => void;
  disabled?: boolean;
}

const inputCls = fieldClass();

const readOnlyCls = cn(fieldClass(), 'cursor-not-allowed bg-ui-subtle text-ui-text2');

/**
 * User-onboarding step 1 — name, profile picture, email, and phone.
 *
 * Email comes from the verified account and is read-only — the user signed up / accepted the
 * invite with it, so there is nothing to re-enter or re-verify here. Phone uses the same
 * dial-code + number layout as the invite forms and is combined into a single international
 * string (`${dialCode}${number}`). It's editable until verified: a new/changed number turns the
 * primary button into "Verify Phone", which sends an SMS code and hands off to the verification
 * screen. A verified, unchanged number is shown read-only.
 */
const ProfileStep: React.FC<ProfileStepProps> = ({
  name,
  email,
  roleName,
  phone,
  phoneVerified,
  avatarUrl,
  onNameChange,
  onAvatarChange,
  onProceed,
  onBack,
  saving = false,
  phoneValue,
  onPhoneValueChange,
  countryCodeValue,
  onSkip,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploadAvatar, { isLoading: uploading }] = useUploadAvatarMutation();
  const [requestPhoneChange, { isLoading: requesting }] = useRequestPhoneChangeMutation();
  const { data: countriesData } = useGetCountriesQuery();

  const [dialCode, setDialCode] = useState('+1');
  const [number, setNumber] = useState('');
  const [error, setError] = useState('');
  const hydrated = useRef(false);

  const dialCodes = countriesData?.data?.map((c) => c.dial_code) ?? [];

  // Hydrate from the already-verified `phone` prop (runs once when country list loads).
  useEffect(() => {
    if (hydrated.current || !countriesData?.data) return;
    const src = phoneValue ?? phone;
    if (src) {
      const { countryCode, number: num } = splitPhone(src, dialCodes);
      if (countryCode) setDialCode(countryCode);
      setNumber(num);
    }
    hydrated.current = true;
  }, [countriesData, phone, phoneValue]); // eslint-disable-line react-hooks/exhaustive-deps

  // Explicit dial code from the agent (set separately) — drives the dropdown directly.
  useEffect(() => {
    const norm = normalizeDialCode(countryCodeValue ?? '');
    if (norm) setDialCode(norm);
  }, [countryCodeValue]);

  // Sync from external phoneValue (the agent filled the number). Local numbers leave the dial code
  // alone; a full international number here is still split as a fallback.
  useEffect(() => {
    if (!phoneValue) return;
    const { countryCode, number: num } = splitPhone(phoneValue, dialCodes);
    if (countryCode) setDialCode(countryCode);
    setNumber(num);
  }, [phoneValue, countriesData]); // eslint-disable-line react-hooks/exhaustive-deps

  const combined = `${dialCode}${number.trim()}`;

  // Notify parent of phone draft changes when controlled mode is active.
  useEffect(() => {
    if (onPhoneValueChange && number.trim()) {
      onPhoneValueChange(combined);
    }
  }, [combined]); // eslint-disable-line react-hooks/exhaustive-deps

  const phoneLocked = phoneVerified && !!(phone?.trim());
  const phoneChanged = combined !== (phone ?? '').trim();
  // A phone needs verifying when one is entered and it's either new or not yet verified.
  const needsPhoneVerify = !!number.trim() && (phoneChanged || !phoneVerified);

  const handleAvatar = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const url = await uploadAvatar(fd).unwrap();
      onAvatarChange(url);
    } catch {
      setError(t('onboarding.steps.failedToUploadPicture'));
    }
  };

  // No phone change needed → continue. New/unverified phone → send the SMS code, then advance.
  const handlePrimary = async () => {
    if (!needsPhoneVerify) {
      onProceed(null);
      return;
    }
    setError('');
    try {
      await requestPhoneChange({ new_phone: combined }).unwrap();
      onProceed(combined);
    } catch (e) {
      setError(getErrorDetail(e) ?? 'Could not send the verification code. Please check the number and try again.');
    }
  };

  const busy = saving || uploading || requesting;

  return (
    <div className="card-surface p-6 sm:p-8">
      <h2 className="text-h3 text-ui-text mb-1">Set up your profile</h2>
      <p className="text-ui-text2 text-sm mb-6">Tell us a little about you. You can change this anytime.</p>

      {/* Avatar — stays interactive during the voice flow (SAM tells the user to tap it to add a
          photo, since it can't take a file by voice), mirroring the brand step's logo upload. */}
      <div className="flex items-center gap-4 mb-6">
        <div className="h-16 w-16 rounded-full bg-ui-subtle overflow-hidden flex items-center justify-center ring-1 ring-ui-border">
          {avatarUrl ? (
            <img src={avatarUrl} alt="Profile" className="h-full w-full object-cover" />
          ) : (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-7 w-7 text-icon">
              <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" /><circle cx="12" cy="7" r="4" />
            </svg>
          )}
        </div>
        <div>
          {/* Label-for is more reliable than button → programmatic .click() in interactive voice
              mode (AudioContext active) where browser user-activation may already be consumed. */}
          <label
            htmlFor="profile-avatar-upload"
            className={`text-sm font-medium text-accent-text hover:underline cursor-pointer ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
          >
            {uploading ? 'Uploading…' : avatarUrl ? 'Change picture' : 'Upload picture'}
          </label>
          <p className="text-xs text-ui-text3 mt-0.5">JPEG, PNG, WebP or SVG, up to 5 MB.</p>
        </div>
        <input ref={fileRef} id="profile-avatar-upload" type="file" accept="image/*" className="hidden" onChange={handleAvatar} disabled={uploading} />
      </div>

      {/* Everything below the avatar is disabled while voice drives the form. */}
      <div className={disabled ? 'pointer-events-none opacity-60 select-none' : ''}>

      {/* Name */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-ui-text mb-1.5">Full Name</label>
        <input type="text" value={name} onChange={(e) => onNameChange(e.target.value)} placeholder="Your full name" className={inputCls} />
      </div>

      {/* Email — read-only, sourced from the verified account */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-ui-text mb-1.5">Email Address</label>
        <input type="email" value={email} readOnly disabled className={readOnlyCls} />
        <p className="text-xs text-ui-text3 mt-1.5">This is the verified email on your account.</p>
      </div>

      {/* Role — read-only, assigned at signup/invite */}
      {roleName && (
        <div className="mb-4">
          <label className="block text-sm font-medium text-ui-text mb-1.5">Role</label>
          <input type="text" value={roleName} readOnly disabled className={readOnlyCls} />
          <p className="text-xs text-ui-text3 mt-1.5">Your role determines what you can access. Contact an admin to change it.</p>
        </div>
      )}

      {/* Phone — read-only once verified, otherwise dial-code + number, editable + verifiable */}
      <div className="mb-2">
        <label className="block text-sm font-medium text-ui-text mb-1.5">
          Phone Number{phoneLocked && <span className="ml-2 text-xs font-normal text-success-text">Verified</span>}
        </label>
        {phoneLocked ? (
          <input type="tel" value={phone ?? ''} readOnly disabled className={readOnlyCls} />
        ) : (
          <div className="flex gap-2">
            <DialCodeSelect value={dialCode} onChange={setDialCode} className="w-32 shrink-0" />
            <input
              type="tel"
              value={number}
              onChange={(e) => setNumber(e.target.value)}
              placeholder="Enter phone number"
              className={inputCls}
            />
          </div>
        )}
        {!phoneLocked && needsPhoneVerify && (
          <p className="text-xs text-ui-text3 mt-1.5">We'll text a code to verify this number on the next screen.</p>
        )}
      </div>

      {error && <p className="text-danger-text text-sm mt-2">{error}</p>}

      <div className="flex gap-3 mt-6">
        {onBack && (
          <Button type="button"
            onClick={onBack}
            variant="outline" size="lg" className="flex-1">
            <BackIcon />
            Back
          </Button>
        )}
        <Button type="button"
          onClick={handlePrimary}
          disabled={busy}
          size="lg" className="flex-1">
          {busy ? 'Please wait…' : (<>{needsPhoneVerify ? 'Verify Phone' : 'Next'} <NextIcon /></>)}
        </Button>
      </div>
      {onSkip && (
        <div className="text-center mt-3">
          <Button type="button"
            onClick={onSkip}
            variant="link">
            Skip for now
          </Button>
        </div>
      )}
      </div>
    </div>
  );
};

export default ProfileStep;
